"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/components/ui/use-toast"
import { supabase } from "@/utils/supabase/client"
import { useAuth } from "@/contexts/auth-context"
import { PatientSearch } from "./patient-search"
import { Patient, Payment, Doctor } from "@/types"
import { Loader2 } from "lucide-react"

interface PaymentFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (payment: Payment) => void;
    paymentToEdit?: Payment | null;
    doctors: Doctor[];
}

export function PaymentFormModal({ isOpen, onClose, onSave, paymentToEdit, doctors }: PaymentFormModalProps) {
    const { toast } = useToast();
    const { currentOrganization } = useAuth();
    const [patient, setPatient] = useState<Patient | null>(null);
    const [totalAmount, setTotalAmount] = useState('');
    const [amountPaid, setAmountPaid] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [status, setStatus] = useState<'completed' | 'pending' | 'partial' | 'failed'>('completed');
    const [isLoading, setIsLoading] = useState(false);
    const [reference, setReference] = useState('');
    const [doctorId, setDoctorId] = useState<string | null>(null);
    const [serviceType, setServiceType] = useState('');
    
    const amountDue = (parseFloat(totalAmount) || 0) - (parseFloat(amountPaid) || 0);

    useEffect(() => {
        if (paymentToEdit && patient) {
            // @ts-ignore
            if (paymentToEdit.patients) {
                // @ts-ignore
                setPatient(paymentToEdit.patients);
            }
            setTotalAmount(paymentToEdit.amount_paid.toString());
            setAmountPaid(paymentToEdit.amount_paid.toString());
            setPaymentMethod(paymentToEdit.payment_method);
            setStatus(paymentToEdit.status as 'completed' | 'pending' | 'partial' | 'failed');
            setReference(paymentToEdit.reference);
            setDoctorId(paymentToEdit.doctor_id);
            setServiceType(paymentToEdit.service_type || '');
        } else {
            setPatient(null);
            setTotalAmount('');
            setAmountPaid('');
            setPaymentMethod('Cash');
            setStatus('completed');
            setReference('');
            setDoctorId(null);
            setServiceType('');
        }
    }, [paymentToEdit, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!patient || !totalAmount || !amountPaid || !paymentMethod || !status || !currentOrganization) {
            toast({
                title: "Error",
                description: "Please fill out all fields.",
                variant: "destructive"
            });
            return;
        }
        setIsLoading(true);

        // Explicitly construct the payment data object according to the Payment type
        // This helps ensure all required fields are present and correctly typed.
        const paymentData: Omit<Payment, 'id' | 'created_at' | 'appointments' | 'patients'> = {
            patient_id: patient.id,
            organization_id: currentOrganization.id,
            doctor_id: doctorId || null,
            amount_paid: parseFloat(amountPaid) || 0,
            amount_due: amountDue > 0 ? amountDue : 0,
            change: amountDue < 0 ? Math.abs(amountDue) : 0,
            payment_method: paymentMethod,
            status: status,
            reference: reference || `WALKIN-${Date.now()}`,
            is_walk_in: true,
            appointment_id: null,
            service_type: serviceType,
            walk_in_patient_name: null,
        };

        console.log("Submitting payment data:", paymentData);

        const { data, error } = await supabase
            .from('payments')
            .insert(paymentData)
            .select()
            .single();

        setIsLoading(false);

        if (error) {
            console.error("Supabase error:", error);
            toast({
                title: "Error processing payment",
                description: error.message,
                variant: "destructive"
            });
        } else {
            toast({
                title: "Payment processed successfully!",
                description: `Payment of Rs. ${amountPaid} for ${patient.name} has been recorded.`,
            });
            onSave(data);
            onClose();
        }
    }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
            <DialogHeader>
                <DialogTitle>{paymentToEdit ? 'Edit Payment' : 'Process a New Payment'}</DialogTitle>
                <DialogDescription>Select a patient and enter payment details.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="p-4 border rounded-lg space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label>Patient</Label>
                        <PatientSearch onPatientSelect={(p) => setPatient(p as Patient | null)} />
                        {patient && (
                            <div className="text-sm p-2 bg-muted rounded-md">
                                Selected: {patient.name} (MR#: {patient.mr_number})
                            </div>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="doctor">Doctor</Label>
                        <Select onValueChange={setDoctorId} value={doctorId || ''}>
                            <SelectTrigger id="doctor"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                            <SelectContent>
                                {doctors.map(doc => <SelectItem key={doc.id} value={doc.id}>{doc.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                  </div>
                   <div className="space-y-2">
                      <Label htmlFor="service-type">Service Details</Label>
                      <Input id="service-type" placeholder="e.g., Consultation, Follow-up" value={serviceType} onChange={e => setServiceType(e.target.value)} />
                   </div>
                </div>

                <div className="p-4 border rounded-lg space-y-4">
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                          <Label htmlFor="total-amount">Total Billable Amount</Label>
                          <Input id="total-amount" type="number" placeholder="e.g., 1500" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} required/>
                      </div>
                      <div className="space-y-2">
                          <Label htmlFor="amount-paid">Amount Paid</Label>
                          <Input id="amount-paid" type="number" placeholder="e.g., 1000" value={amountPaid} onChange={e => setAmountPaid(e.target.value)} required/>
                      </div>
                    </div>
                    <div className="text-right font-medium">
                        Amount Due: <span className="text-red-500">Rs. {amountDue > 0 ? amountDue.toLocaleString() : 0}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                          <Label htmlFor="payment-method">Payment Method</Label>
                          <Select onValueChange={setPaymentMethod} value={paymentMethod}>
                              <SelectTrigger id="payment-method"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                  <SelectItem value="Cash">Cash</SelectItem>
                                  <SelectItem value="Credit Card">Credit Card</SelectItem>
                              </SelectContent>
                          </Select>
                      </div>
                      <div className="space-y-2">
                          <Label htmlFor="status">Status</Label>
                          <Select onValueChange={(v) => setStatus(v as 'completed' | 'pending' | 'partial' | 'failed')} value={status}>
                              <SelectTrigger id="status"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="partial">Partial</SelectItem>
                                  <SelectItem value="failed">Failed</SelectItem>
                              </SelectContent>
                          </Select>
                      </div>
                   </div>
                </div>
                
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                    <Button type="submit" disabled={isLoading}>
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Process Payment
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
  )
} 